import { CSSProperties } from "react";
import { LayoutTypes, TestimonialCardStyleKit } from "./types";

const clamp = (maxSize: string, min = "0.85rem", vw = "1.1vw") =>
  `clamp(${min}, ${vw}, ${maxSize})`;

const scaleRem = (v: string, factor: number) => {
  const m = /([0-9]*\.?[0-9]+)rem/.exec(v);
  if (!m) return v;
  const num = parseFloat(m[1]) * factor;
  return `${Math.max(0.5, Number(num.toFixed(3)))}rem`;
};

const scalePx = (v: string, factor: number) => {
  const m = /([0-9]*\.?[0-9]+)px/.exec(v);
  if (!m) return v;
  const num = parseFloat(m[1]) * factor;
  return `${Math.max(8, Math.round(num))}px`;
};

export const extractStyles = (
  styles: TestimonialCardStyleKit,
): Record<string, CSSProperties> => {
  const { card, avatar, name, bio, content, date, rating } = styles;

  // Compact visual rhythm and proportions
  const cappedAvatar = Math.min(52, Math.max(36, avatar.size));
  const padMax = scaleRem(card.padding, 0.9);
  const nameMax = scaleRem(name.fontSize, 0.94);
  const bioMax = scaleRem(bio.fontSize, 0.92);
  const contentMax = scaleRem(content.fontSize, 0.95);
  const dateMax = scaleRem(date.fontSize, 0.9);
  const ratingSize = scalePx(rating.size, 0.9);

  const scopedFontFamily =
    content.fontFamily || name.fontFamily || bio.fontFamily || "inherit";

  return {
    cardStyles: {
      outline: `${card.outlineWidth} solid ${card.outlineColor}`,
      padding: clamp(padMax, "0.5rem", "1vw"),
      borderRadius: card.borderRadius,
      boxShadow: card.shadow,
      backgroundColor: card.mode === "light" ? "#fff" : "#1a1a1a",
      fontFamily: scopedFontFamily,
      ...getLayoutStyles(card.layout),
    },

    avatarStyles: {
      width: cappedAvatar,
      height: cappedAvatar,
      borderRadius: avatar.borderRadius,
      display: avatar.visibility === "hidden" ? "none" : "block",
      objectFit: "cover" as const,
    },

    nameStyles: {
      color: name.color,
      fontWeight: name.fontWeight,
      fontSize: clamp(nameMax, "0.9rem", "1vw"),
      fontFamily: name.fontFamily,
      margin: 0,
    },

    bioStyles: {
      color: bio.color,
      fontWeight: bio.fontWeight,
      fontSize: clamp(bioMax, "0.8rem", "0.95vw"),
      fontFamily: bio.fontFamily,
      margin: "0.15rem 0 0 0",
    },

    contentStyles: {
      color: content.color,
      fontWeight: content.fontWeight,
      fontSize: clamp(contentMax, "0.9rem", "1vw"),
      fontFamily: content.fontFamily,
      lineHeight: content.lineHeight,
      margin: "0 0 0.7rem 0",
    },

    dateStyles: {
      color: date.color,
      fontWeight: date.fontWeight,
      fontSize: clamp(dateMax, "0.7rem", "0.85vw"),
      fontFamily: date.fontFamily,
      margin: 0,
      display: date.visibility ? "block" : "none",
    },

    ratingStyles: {
      color: rating.color,
      border: rating.outlineWidth,
      borderColor: rating.outlineColor,
      height: ratingSize,
      width: ratingSize,
    },
  };
};

export const getLayoutStyles = (layout: LayoutTypes) => {
  const layouts: Record<LayoutTypes, CSSProperties> = {
    [LayoutTypes.STACK]: {
      display: "flex",
      flexDirection: "column",
      gap: "0.6rem",
    },
    [LayoutTypes.SPLIT]: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "0.6rem",
    },
    [LayoutTypes.COMPACT]: {
      display: "flex",
      flexDirection: "column",
      gap: "0.5rem",
    },
    [LayoutTypes.CENTERED]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      gap: "0.6rem",
    },
  };
  return layouts[layout] || layouts[LayoutTypes.STACK];
};
